import { useEffect, memo, ReactNode, useRef, useState } from 'react';
import { Pagination } from '../Pagination';

export interface TableColumn<T = any> {
  header: ReactNode;
  width: string;
  align?: 'left' | 'center' | 'right';
  flexGrow?: boolean;
  allowWrap?: boolean;
  renderCell: (item: T, index: number) => ReactNode;
}

export interface TableProps<T = any> {
  data: T[];
  columns: TableColumn<T>[];
  getRowKey: (item: T, index: number) => string | number;
  onRowClick?: (item: T, index: number) => void;
  usePagination?: boolean;
  pageSize?: number;
  emptyMessage?: string;
  className?: string;
  loadingMore?: boolean;
}

function TableComponent<T = any>({
  data,
  columns,
  getRowKey,
  onRowClick,
  usePagination = false,
  pageSize = 10,
  emptyMessage = 'No data available',
  className = '',
  loadingMore = false,
}: TableProps<T>) {
  const [showScrollShadowRight, setShowScrollShadowRight] = useState(false);
  const [showScrollShadowLeft, setShowScrollShadowLeft] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Check if table has horizontal scroll
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const checkScroll = () => {
      const hasScroll = wrapper.scrollWidth > wrapper.clientWidth;
      const scrollLeft = wrapper.scrollLeft;
      const scrollRight = wrapper.scrollWidth - wrapper.clientWidth - scrollLeft;
      
      setShowScrollShadowLeft(hasScroll && scrollLeft > 1);
      setShowScrollShadowRight(hasScroll && scrollRight > 1);
    };

    checkScroll();
    wrapper.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      wrapper.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [data]);

  const getColumnStyle = (column: TableColumn<T>) => ({
    width: column.width,
    minWidth: column.width, 
    textAlign: column.align || 'left',
    ...(column.flexGrow ? { flexGrow: 1 } : {}),
  });

  const renderTable = (displayData: T[]) => (
    <div className={`bg-white rounded-lg shadow-sm overflow-hidden ${className}`}>
      <div className={`relative ${showScrollShadowRight ? 'after:absolute after:top-0 after:right-0 after:bottom-0 after:w-8 after:bg-gradient-to-l after:from-white after:to-transparent after:pointer-events-none' : ''} ${showScrollShadowLeft ? 'before:absolute before:top-0 before:left-0 before:bottom-0 before:w-8 before:bg-gradient-to-r before:from-white before:to-transparent before:pointer-events-none before:z-10' : ''}`}>
        <div 
          ref={wrapperRef}
          className="overflow-x-auto"
        >
          <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((column, index) => (
                <th 
                  key={index}
                  className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                  style={getColumnStyle(column)}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayData.length === 0 ? (
              <tr>
                <td 
                  colSpan={columns.length} 
                  className="p-4 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              displayData.map((item, index) => (
                <tr 
                  key={getRowKey(item, index)} 
                  className={`transition-colors hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(item, index)}
                >
                  {columns.map((column, colIndex) => (
                    <td 
                      key={colIndex}
                      className={`p-4 ${column.allowWrap ? '' : 'whitespace-nowrap'}`}
                      style={getColumnStyle(column)}
                    >
                      <div 
                        className="flex items-center"
                        style={{
                          justifyContent: 
                            column.align === 'right' ? 'flex-end' :
                            column.align === 'center' ? 'center' :
                            'flex-start'
                        }}
                      >
                        {column.renderCell(item, index)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );

  return usePagination ? (
    <Pagination data={data} pageSize={pageSize} loadingMore={loadingMore} getItemKey={(item) => getRowKey(item, 0)}>
      {(paginatedData) => renderTable(paginatedData)}
    </Pagination>
  ) : (
    renderTable(data)
  );
}

// Export as generic component with proper typing
export const Table = memo(TableComponent) as typeof TableComponent;
